import React, {useContext, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useAuth} from "../../../hooks/useAuth";
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {NotificationType} from "../../../constants/global";
import {ToasterContext} from "../../shared/Toaster/ToasterContext";
import {routeConfigs} from "../../../navigation/routeConfig";
import {CircularProgress} from "@mui/material";

interface IConfirmSignUpProps {
  setIsConfirmed: (confirmed: boolean) => void,
  username: string;
  password: string;
}

const ConfirmSignUp: React.FunctionComponent<IConfirmSignUpProps> = ({
  setIsConfirmed,
  username,
  password,
}) => {
  const {addToast} = useContext(ToasterContext);
  const auth: any = useAuth();
  const navigate  = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required('Code Required'),
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const handleOnChange = (res: string) => {
    setCode(res);
  };

  const confirmSignUp = async () => {
    setLoading(true);
    const result = await auth.confirmSignUp(username, code, password);

    if (result.success) {
      setLoading(false);
      setIsConfirmed(false);
      addToast({ type: NotificationType.Success, title: 'Your account has been successfully confirmed.'  });
      navigate ('/');
    } else {
      setLoading(false);
      setIsConfirmed(true);
      addToast({ type: NotificationType.Error, title: result.message  });
    }
  }

  async function resendConfirmationCode() {
    setConfirmLoading(true);
    const result = await auth.resendSignUp(username);

    if (result.success) {
      setConfirmLoading(false);
      addToast({ type: NotificationType.Success, title: 'The new code has been sent.'  });
    } else {
      setConfirmLoading(false);
      addToast({ type: NotificationType.Error, title: result.message  });
    }
  }

  return (
    <>
      <Typography component="h1" variant="h5">
        Verify Code
      </Typography>
      <Box sx={{mt: 2}} component="form" onSubmit={handleSubmit(confirmSignUp)} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="code"
          label="Verify Code"
          autoComplete="code"
          helperText={errors.code?.message?.toString()}
          {...register('code')}
          error={!!errors.code}
          autoFocus
          onChange={(event)=>handleOnChange(event.currentTarget.value)}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{mt: 3, mb: 2}}
        >
          Verify
          { loading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
        </Button>
        <Button
          type="button"
          onClick={resendConfirmationCode}
          fullWidth
          variant="outlined"
          sx={{mt: 3, mb: 2}}
        >
          Resend Code
          { confirmLoading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
        </Button>
        <Grid container>
          <Grid item>
            Already have an account?
            <Link href={routeConfigs.login.path} sx={{ml: 1}} variant="body2">
              Sign In
            </Link>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default ConfirmSignUp;